import { Controller } from "react-hook-form";

import { Label } from "@/components/field";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { joinTags, splitTags, TagInput } from "@/components/tag-input";
import { CollaboratorsSelect } from "@/features/request/create/shared/components/CollaboratorsSelect.tsx";
import { PreferredDesignersSelect } from "@/features/request/create/shared/components/PreferredDesignersSelect.tsx";
import { AiAssetsToggle } from "@/features/request/shared/components/AiAssetsToggle";
import {
  FILE_TYPE_SUGGESTIONS,
  SIZE_SUGGESTIONS,
} from "@/features/shared/constants/technicalDetailsConstants";
import { useUser } from "@/hooks/useUser";

import { useCreateWebsiteDesignRequestFormContext } from "../useCreateWebsiteDesignRequestForm";
import { ComplexitySelect } from "./ComplexitySelect";

export function TechnicalDetailsSection() {
  const { data: user } = useUser();
  const methods = useCreateWebsiteDesignRequestFormContext();

  const showComplexityField = [
    "customer-success",
    "super-admin",
    "team-leader",
  ].includes(user?.user.roleName || "");

  return (
    <>
      <div className="rounded-xl bg-bg-secondary p-3xl">
        <div className="flex flex-col gap-y-lg lg:grid lg:grid-cols-12 lg:gap-x-3xl">
          <span className="col-span-12 text-lg font-semibold text-text-primary">
            Technical Details
          </span>
          <div className="col-span-12">
            <AiAssetsToggle control={methods.control} name="restrictAiAssets" />
          </div>
          <Controller
            control={methods.control}
            name="creativeDirection"
            render={({ field: { name, onBlur, onChange, value } }) => (
              <Select
                className="col-span-6"
                name={name}
                onBlur={onBlur}
                onSelectionChange={onChange}
                placeholder="Select creative direction"
                selectedKey={value}
              >
                <Label>Creative direction</Label>
                <SelectButton />
                <SelectPopover>
                  <SelectListBox>
                    <SelectListItem id="Designer has creative freedom">
                      Designer has creative freedom
                    </SelectListItem>
                    <SelectListItem id="Follow brief exactly">
                      Follow brief exactly
                    </SelectListItem>
                  </SelectListBox>
                </SelectPopover>
              </Select>
            )}
          />
          <Controller
            control={methods.control}
            name="sizesNeeded"
            render={({
              field: { name, onBlur, onChange, value },
              fieldState: { error, invalid },
            }) => (
              <TagInput
                className="col-span-6"
                errorMessage={error?.message}
                isInvalid={invalid}
                label="Sizes needed"
                name={name}
                onBlur={onBlur}
                onChange={(tags) => onChange(joinTags(tags))}
                placeholder="E.g. 1080x1080, 16x9, etc."
                requiredHint
                suggestions={SIZE_SUGGESTIONS}
                value={splitTags(value)}
              />
            )}
          />
          <Controller
            control={methods.control}
            name="preferredFileTypes"
            render={({
              field: { name, onBlur, onChange, value },
              fieldState: { error, invalid },
            }) => (
              <TagInput
                className={showComplexityField ? "col-span-4" : "col-span-6"}
                errorMessage={error?.message}
                isInvalid={invalid}
                label="File types"
                name={name}
                onBlur={onBlur}
                onChange={(tags) => onChange(joinTags(tags))}
                placeholder="E.g. JPG, PNG, PDF, etc."
                requiredHint
                suggestions={FILE_TYPE_SUGGESTIONS.graphic}
                value={splitTags(value)}
              />
            )}
          />
          <PreferredDesignersSelect
            className={showComplexityField ? "col-span-4" : "col-span-6"}
            methods={methods}
          />
          {showComplexityField && <ComplexitySelect />}
        </div>
      </div>
      <div className="w-1/2 rounded-xl bg-bg-secondary p-3xl">
        <CollaboratorsSelect methods={methods} />
      </div>
    </>
  );
}
